/**
 * Transaction Validation Utility
 * Validates data before executing transactions to prevent partial saves
 */

import { z } from 'zod'

/**
 * Validation schemas for different transaction types
 */
// Helper to normalize values (convert null/undefined to empty string for string fields)
const normalizeValue = (value) => {
  if (value === null || value === undefined) return ''
  return String(value)
}

const VALIDATION_SCHEMAS = {
  examination: z.object({
    client: z.object({
      first_name: z.string().min(1, 'First name is required'),
      last_name: z.string().min(1, 'Last name is required'),
      date_of_birth: z.string().min(1, 'Date of birth is required'),
      gender: z.string().min(1, 'Gender is required'),
      email: z.union([z.string().email(), z.string().length(0), z.null()]).optional(),
      phone: z.union([z.string(), z.null()]).optional(),
    }),
    medicalHistory: z.object({
      primary_goals: z.string().min(1, 'Primary goals are required'),
    }),
    vitalSigns: z.object({
      bloodPressure: z.preprocess(
        (val) => normalizeValue(val),
        z.string().min(1, 'Blood pressure is required')
      ),
      heartRate: z.preprocess(
        (val) => normalizeValue(val),
        z.string().min(1, 'Heart rate is required')
      ),
    }),
    customQuestions: z.array(z.any()).optional(),
  }),

  carePlan: z.object({
    carePlan: z.object({
      client_id: z.string().uuid('Invalid client ID'),
      title: z.string().min(1, 'Title is required'),
      description: z.string().optional(),
    }),
    therapies: z.array(z.any()).optional(),
  }),

  therapy: z.object({
    client_id: z.string().uuid('Invalid client ID'),
    care_plan_id: z.string().uuid('Invalid care plan ID').optional(),
    therapy_type: z.string().min(1, 'Therapy type is required'),
    scheduled_date: z.string().min(1, 'Scheduled date is required'),
  }),

  session: z.object({
    client_id: z.string().uuid('Invalid client ID'),
    therapy_id: z.string().uuid('Invalid therapy ID').optional(),
    session_date: z.string().min(1, 'Session date is required'),
    notes: z.string().optional(),
  }),
}

/**
 * Validate transaction data before execution
 */
export const validateTransactionData = (transactionType, data) => {
  const schema = VALIDATION_SCHEMAS[transactionType]

  if (!schema) {
    console.warn(`No validation schema for transaction type: ${transactionType}`)
    return { valid: true, errors: [] }
  }

  try {
    schema.parse(data)
    return { valid: true, errors: [] }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map((err) => ({
        path: err.path.join('.'),
        message: err.message,
      }))
      return { valid: false, errors }
    }
    return { valid: false, errors: [{ path: 'unknown', message: error.message }] }
  }
}

/**
 * Validate all operations in a transaction
 */
export const validateTransactionOperations = (operations) => {
  const errors = []

  if (!operations || operations.length === 0) {
    errors.push({ path: 'operations', message: 'No operations provided' })
    return { valid: false, errors }
  }

  operations.forEach((operation, index) => {
    // Validate operation structure
    if (!operation.table) {
      errors.push({ path: `operations[${index}].table`, message: 'Table name is required' })
    }

    if (!operation.action) {
      errors.push({ path: `operations[${index}].action`, message: 'Action is required' })
    } else if (!['insert', 'update', 'upsert', 'delete'].includes(operation.action)) {
      errors.push({
        path: `operations[${index}].action`,
        message: `Invalid action: ${operation.action}`,
      })
    }

    // Validate data for insert/update/upsert
    if (['insert', 'update', 'upsert'].includes(operation.action)) {
      if (!operation.data || typeof operation.data !== 'object') {
        errors.push({
          path: `operations[${index}].data`,
          message: 'Data is required for insert/update/upsert operations',
        })
      }
    }

    // Validate filters for update/delete
    if (['update', 'delete'].includes(operation.action)) {
      if (!operation.filters || Object.keys(operation.filters).length === 0) {
        errors.push({
          path: `operations[${index}].filters`,
          message: 'Filters are required for update/delete operations',
        })
      }
    }
  })

  return { valid: errors.length === 0, errors }
}

/**
 * Validate foreign key relationships
 */
export const validateForeignKeyRelations = async (operations, organizationId) => {
  const errors = []

  for (const operation of operations) {
    // Check if referenced records exist
    if (operation.data) {
      // Check client_id references
      if (operation.data.client_id) {
        const { data: client, error } = await supabase
          .from('clients')
          .select('id')
          .eq('id', operation.data.client_id)
          .eq('organization_id', organizationId)
          .single()

        if (error || !client) {
          errors.push({
            path: `${operation.table}.client_id`,
            message: `Referenced client not found: ${operation.data.client_id}`,
          })
        }
      }

      // Check care_plan_id references
      if (operation.data.care_plan_id) {
        const { data: carePlan, error } = await supabase
          .from('care_plans')
          .select('id')
          .eq('id', operation.data.care_plan_id)
          .eq('organization_id', organizationId)
          .single()

        if (error || !carePlan) {
          errors.push({
            path: `${operation.table}.care_plan_id`,
            message: `Referenced care plan not found: ${operation.data.care_plan_id}`,
          })
        }
      }

      // Check therapy_id references
      if (operation.data.therapy_id) {
        const { data: therapy, error } = await supabase
          .from('therapies')
          .select('id')
          .eq('id', operation.data.therapy_id)
          .eq('organization_id', organizationId)
          .single()

        if (error || !therapy) {
          errors.push({
            path: `${operation.table}.therapy_id`,
            message: `Referenced therapy not found: ${operation.data.therapy_id}`,
          })
        }
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Comprehensive transaction validation
 */
export const validateTransaction = async (transactionType, data, operations, organizationId) => {
  const validationResults = {
    valid: true,
    errors: [],
    warnings: [],
  }

  // 1. Validate transaction data structure
  if (transactionType && data) {
    const dataValidation = validateTransactionData(transactionType, data)
    if (!dataValidation.valid) {
      validationResults.valid = false
      validationResults.errors.push(...dataValidation.errors)
    }
  }

  // 2. Validate operations structure
  if (operations) {
    const operationsValidation = validateTransactionOperations(operations)
    if (!operationsValidation.valid) {
      validationResults.valid = false
      validationResults.errors.push(...operationsValidation.errors)
    }
  }

  // 3. Validate foreign key relationships (async)
  if (operations && organizationId) {
    const fkValidation = await validateForeignKeyRelations(operations, organizationId)
    if (!fkValidation.valid) {
      validationResults.valid = false
      validationResults.errors.push(...fkValidation.errors)
    }
  }

  return validationResults
}

export default {
  validateTransactionData,
  validateTransactionOperations,
  validateForeignKeyRelations,
  validateTransaction,
}

